import { TalentStatus } from "../../../talents/types/common";
import { Candidate } from "../../TalentsStatusAPI";
import { CandidateStatus, CandidateStatusAction } from "../constants";

interface Props {
  candidate: Candidate;
  orderCancelled?: boolean;
  orderFulfilled?: boolean;
  orderedAmountOffered?: boolean;
}

const getStatus = (candidate: Candidate): CandidateStatus => {
  if (candidate.withdrew) {
    return CandidateStatus.Withdrew;
  }
  if (candidate.eezyRejected) {
    return CandidateStatus.EezyRejected;
  }
  if (candidate.workAgreementAccepted) {
    return CandidateStatus.WorkAgreementAccepted;
  }
  if (candidate.offered) {
    return candidate.offered.success
      ? CandidateStatus.Offered
      : CandidateStatus.Rejected;
  }
  if (candidate.customerApproved) {
    return candidate.customerApproved.success
      ? CandidateStatus.CustomerApproved
      : CandidateStatus.CustomerRejected;
  }
  if (candidate.chosenForInterview) {
    return CandidateStatus.ChosenForInterview;
  }
  if (candidate.introduced) {
    return CandidateStatus.Introduced;
  }
  if (candidate.interested) {
    return CandidateStatus.Interested;
  }
  if (candidate.interestAsked) {
    return CandidateStatus.InterestAsked;
  }
  if (candidate.picked) {
    return CandidateStatus.Picked;
  }
  return CandidateStatus.Default;
};

const isJobAdApplicant = (candidate: Candidate) => !!candidate.applieadAt;

const getOpenOrderActions = (
  candidate: Candidate,
  status: CandidateStatus,
): CandidateStatusAction[] => {
  const jobAdApplicant = isJobAdApplicant(candidate);

  if (status === CandidateStatus.Default && jobAdApplicant) {
    return [
      CandidateStatusAction.PICK,
      CandidateStatusAction.CHOOSE_FOR_INTRODUCTION,
      CandidateStatusAction.SEND_WORK_OFFER,
      CandidateStatusAction.NOT_SUITABLE,
      CandidateStatusAction.WITHDREW,
    ];
  }

  if (status === CandidateStatus.Default && !jobAdApplicant) {
    return [
      CandidateStatusAction.PICK,
      CandidateStatusAction.ASK_FOR_INTEREST,
      CandidateStatusAction.CHOOSE_FOR_INTRODUCTION,
      CandidateStatusAction.NOT_SUITABLE,
      CandidateStatusAction.WITHDREW,
    ];
  }

  if (status === CandidateStatus.Picked && jobAdApplicant) {
    return [
      CandidateStatusAction.CHOOSE_FOR_INTRODUCTION,
      CandidateStatusAction.SEND_WORK_OFFER,
      CandidateStatusAction.NOT_SUITABLE,
      CandidateStatusAction.WITHDREW,
    ];
  }

  if (status === CandidateStatus.Picked && !jobAdApplicant) {
    return [
      CandidateStatusAction.ASK_FOR_INTEREST,
      CandidateStatusAction.CHOOSE_FOR_INTRODUCTION,
      CandidateStatusAction.SEND_WORK_OFFER,
      CandidateStatusAction.NOT_SUITABLE,
      CandidateStatusAction.WITHDREW,
    ];
  }

  if (
    status === CandidateStatus.InterestAsked ||
    status === CandidateStatus.Interested
  ) {
    return [
      CandidateStatusAction.CHOOSE_FOR_INTRODUCTION,
      CandidateStatusAction.SEND_WORK_OFFER,
      CandidateStatusAction.NOT_SUITABLE,
      CandidateStatusAction.WITHDREW,
    ];
  }

  if (status === CandidateStatus.Introduced) {
    return [
      CandidateStatusAction.CANCEL_INTRODUCTION,
      CandidateStatusAction.CHOOSE_FOR_INTERVIEW,
      CandidateStatusAction.CUSTOMER_APPROVED,
      CandidateStatusAction.CUSTOMER_REJECTED,
      CandidateStatusAction.SEND_WORK_OFFER,
      CandidateStatusAction.NOT_SUITABLE,
      CandidateStatusAction.WITHDREW,
    ];
  }

  if (status === CandidateStatus.ChosenForInterview && candidate.introduced) {
    return [
      CandidateStatusAction.CANCEL_INTRODUCTION,
      CandidateStatusAction.CUSTOMER_APPROVED,
      CandidateStatusAction.CUSTOMER_REJECTED,
      CandidateStatusAction.SEND_WORK_OFFER,
      CandidateStatusAction.NOT_SUITABLE,
      CandidateStatusAction.WITHDREW,
    ];
  }

  if (status === CandidateStatus.ChosenForInterview && !candidate.introduced) {
    return [
      CandidateStatusAction.CUSTOMER_APPROVED,
      CandidateStatusAction.CUSTOMER_REJECTED,
      CandidateStatusAction.SEND_WORK_OFFER,
      CandidateStatusAction.NOT_SUITABLE,
      CandidateStatusAction.WITHDREW,
    ];
  }

  if (status === CandidateStatus.CustomerApproved) {
    return [
      CandidateStatusAction.CUSTOMER_REJECTED,
      CandidateStatusAction.SEND_WORK_OFFER,
      CandidateStatusAction.NOT_SUITABLE,
      CandidateStatusAction.WITHDREW,
    ];
  }

  if (status === CandidateStatus.CustomerRejected) {
    return [
      CandidateStatusAction.CUSTOMER_APPROVED,
      CandidateStatusAction.SEND_WORK_OFFER,
      CandidateStatusAction.NOT_SUITABLE,
      CandidateStatusAction.WITHDREW,
    ];
  }

  if (status === CandidateStatus.Rejected) {
    return [
      CandidateStatusAction.SEND_WORK_OFFER,
      CandidateStatusAction.NOT_SUITABLE,
      CandidateStatusAction.WITHDREW,
    ];
  }

  if (status === CandidateStatus.Offered) {
    return [CandidateStatusAction.WITHDREW];
  }

  if (status === CandidateStatus.WorkAgreementAccepted) {
    return [];
  }

  if (status === CandidateStatus.Withdrew) {
    return [CandidateStatusAction.CANCEL_WITHDRAWAL];
  }

  return [];
};

const getRejectionMessageActions = (
  candidate: Candidate,
): CandidateStatusAction[] => {
  if (candidate.rejectionMessageSent) {
    return [];
  }

  if (candidate.eezyRejected) {
    if (
      isJobAdApplicant(candidate) ||
      (candidate.interestAsked && candidate.interested?.success) ||
      candidate.introduced ||
      candidate.chosenForInterview
    ) {
      return [
        CandidateStatusAction.SEND_REJECTED_MESSAGE,
        CandidateStatusAction.CANCEL_REJECTION,
      ];
    }

    return [CandidateStatusAction.CANCEL_REJECTION];
  }

  return [];
};

const getNonActiveTalentActions = (
  status: CandidateStatus,
): CandidateStatusAction[] => {
  if (status === CandidateStatus.Picked) {
    return [CandidateStatusAction.NOT_SUITABLE, CandidateStatusAction.WITHDREW];
  }

  if (status === CandidateStatus.Withdrew) {
    return [CandidateStatusAction.CANCEL_WITHDRAWAL];
  }

  return [
    CandidateStatusAction.PICK,
    CandidateStatusAction.NOT_SUITABLE,
    CandidateStatusAction.WITHDREW,
  ];
};

const getClosedOrderActions = (
  candidate: Candidate,
  status: CandidateStatus,
): CandidateStatusAction[] => {
  if (status === CandidateStatus.WorkAgreementAccepted) {
    return [];
  }

  if (status === CandidateStatus.Default && !isJobAdApplicant(candidate)) {
    return [];
  }

  return [CandidateStatusAction.NOT_SUITABLE];
};

export const mapActionsFor = ({
  candidate,
  orderCancelled = false,
  orderFulfilled = false,
  orderedAmountOffered = false,
}: Props): CandidateStatusAction[] => {
  const status = getStatus(candidate);

  if (status === CandidateStatus.EezyRejected) {
    return getRejectionMessageActions(candidate);
  }

  if (candidate.status !== TalentStatus.ACTIVE) {
    return getNonActiveTalentActions(status);
  }

  if (orderCancelled || orderFulfilled) {
    return getClosedOrderActions(candidate, status);
  }

  const actions = getOpenOrderActions(candidate, status);

  if (orderedAmountOffered) {
    return actions.filter(
      (action) => action !== CandidateStatusAction.SEND_WORK_OFFER,
    );
  }

  return actions;
};
